import React, { useState, useEffect, useCallback } from "react";
import { developmentApi } from "./api";

const COLORS = {
  surface: "rgba(255,255,255,0.025)",
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

const TOOLS = [
  {
    id: "pivot",
    icon: "🔀",
    label: "Pivot Suggestor",
    desc: "3 stronger versions of your idea",
    minPlan: "free",
  },
  {
    id: "competitors",
    icon: "🗺️",
    label: "Competitor Mapper",
    desc: "Who's already doing this",
    minPlan: "pro",
  },
  {
    id: "icp",
    icon: "🎯",
    label: "ICP Generator",
    desc: "Define your ideal customer",
    minPlan: "starter",
  },
  {
    id: "landing-page",
    icon: "🚀",
    label: "Landing Page",
    desc: "MVP landing page in seconds",
    minPlan: "pro",
  },
  {
    id: "pitch-deck",
    icon: "📊",
    label: "Pitch Deck",
    desc: "Investor-ready slide deck",
    minPlan: "pro",
  },
];

const LOADING_LABELS = {
  pivot: "Generating pivot ideas…",
  competitors: "Mapping competitors…",
  icp: "Building customer profile…",
  "landing-page": "Designing landing page…",
  "pitch-deck": "Crafting pitch deck…",
};

function planRank(plan) {
  const ranks = { free: 0, starter: 1, pro: 2, team: 3 };
  return ranks[plan] ?? 0;
}

function canAccess(userPlan, minPlan) {
  return planRank(userPlan) >= planRank(minPlan);
}

function planLabel(minPlan) {
  if (minPlan === "pro") return "Pro";
  if (minPlan === "starter") return "Starter";
  return "Free";
}

function PivotView({ data, onUsePivot }) {
  return (
    <div>
      <p style={{ fontSize: 14, color: COLORS.muted, marginBottom: 20, lineHeight: 1.6 }}>{data.summary}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {data.pivots?.map((pivot, i) => (
          <div
            key={i}
            style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 14,
              padding: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <h4 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>{pivot.title}</h4>
              <button
                onClick={() => onUsePivot(pivot.idea)}
                style={{
                  background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 14px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Use this →
              </button>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: COLORS.text, margin: "0 0 10px" }}>{pivot.idea}</p>
            <p style={{ fontSize: 13, lineHeight: 1.55, color: COLORS.muted, margin: 0 }}>
              <strong style={{ color: COLORS.amber }}>Why:</strong> {pivot.rationale}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompetitorsView({ data }) {
  return (
    <div>
      <p style={{ fontSize: 14, color: COLORS.muted, marginBottom: 20, lineHeight: 1.6 }}>{data.market_summary}</p>

      <h4 style={{ fontSize: 13, color: COLORS.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
        Direct Competitors
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {data.direct_competitors?.map((c, i) => (
          <div key={i} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <strong style={{ color: COLORS.text }}>{c.name}</strong>
              {c.url && (
                <a href={c.url.startsWith("http") ? c.url : `https://${c.url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: COLORS.indigo }}>
                  Visit →
                </a>
              )}
            </div>
            <p style={{ fontSize: 13, color: COLORS.muted, margin: "0 0 8px" }}>{c.positioning}</p>
            <p style={{ fontSize: 12, color: COLORS.green, margin: 0 }}>+ {c.strength}</p>
            <p style={{ fontSize: 12, color: COLORS.red, margin: "4px 0 0" }}>− {c.weakness}</p>
          </div>
        ))}
      </div>

      {data.white_space && (
        <div style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.green, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
            White Space
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: COLORS.text, margin: 0 }}>{data.white_space}</p>
        </div>
      )}

      {data.differentiation_playbook?.length > 0 && (
        <div>
          <h4 style={{ fontSize: 13, color: COLORS.indigo, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
            Differentiation Playbook
          </h4>
          <ol style={{ margin: 0, paddingLeft: 20, color: COLORS.text, fontSize: 14, lineHeight: 1.8 }}>
            {data.differentiation_playbook.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function IcpView({ data }) {
  const icp = data.primary_icp || {};
  return (
    <div>
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <h4 style={{ margin: "0 0 8px", fontSize: 18, color: COLORS.text }}>{icp.name}</h4>
        <p style={{ fontSize: 13, color: COLORS.muted, margin: "0 0 16px" }}>{icp.demographics}</p>

        {icp.pain_points?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Pain Points</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: COLORS.text, lineHeight: 1.7 }}>
              {icp.pain_points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        {icp.goals?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.green, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Goals</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: COLORS.text, lineHeight: 1.7 }}>
              {icp.goals.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          </div>
        )}

        {icp.budget_range && (
          <p style={{ fontSize: 13, color: COLORS.muted, margin: "0 0 8px" }}>
            <strong style={{ color: COLORS.amber }}>Budget:</strong> {icp.budget_range}
          </p>
        )}
      </div>

      {data.messaging_hook && (
        <div style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.indigo, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Messaging Hook</div>
          <p style={{ fontSize: 15, fontStyle: "italic", color: COLORS.text, margin: 0, lineHeight: 1.6 }}>"{data.messaging_hook}"</p>
        </div>
      )}

      {data.channels?.length > 0 && (
        <div>
          <h4 style={{ fontSize: 13, color: COLORS.indigo, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Go-to-Market Channels</h4>
          {data.channels.map((ch, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
              <span style={{
                fontSize: 10,
                textTransform: "uppercase",
                fontWeight: 700,
                padding: "3px 8px",
                borderRadius: 6,
                background: ch.priority === "high" ? "rgba(16,185,129,0.15)" : ch.priority === "medium" ? "rgba(245,158,11,0.15)" : "rgba(148,163,184,0.1)",
                color: ch.priority === "high" ? COLORS.green : ch.priority === "medium" ? COLORS.amber : COLORS.muted,
                flexShrink: 0,
              }}>
                {ch.priority}
              </span>
              <div>
                <strong style={{ fontSize: 14, color: COLORS.text }}>{ch.channel}</strong>
                <p style={{ fontSize: 13, color: COLORS.muted, margin: "2px 0 0" }}>{ch.tactic}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LandingPageView({ data }) {
  const [showHtml, setShowHtml] = useState(false);

  const copyHtml = () => {
    if (data.html) navigator.clipboard?.writeText(data.html);
  };

  const openPreview = () => {
    if (!data.html) return;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(data.html);
      w.document.close();
    }
  };

  return (
    <div>
      <div style={{ textAlign: "center", padding: "24px 0", marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 28, color: COLORS.text, fontWeight: 700 }}>{data.headline}</h3>
        <p style={{ margin: "0 0 16px", fontSize: 16, color: COLORS.muted }}>{data.subheadline}</p>
        <span style={{
          display: "inline-block",
          background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
          color: "#fff",
          padding: "10px 24px",
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 600,
        }}>
          {data.cta_text}
        </span>
      </div>

      {data.sections?.map((section, i) => (
        <div key={i} style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 14, color: COLORS.indigo, marginBottom: 8 }}>{section.title}</h4>
          {section.body && <p style={{ fontSize: 14, lineHeight: 1.65, color: COLORS.text, margin: 0 }}>{section.body}</p>}
          {section.items && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 14, color: COLORS.text, lineHeight: 1.7 }}>
              {section.items.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
          )}
        </div>
      ))}

      {data.html && (
        <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
          <button onClick={openPreview} style={actionBtnStyle}>Preview page →</button>
          <button onClick={copyHtml} style={secondaryBtnStyle}>Copy HTML</button>
          <button onClick={() => setShowHtml(!showHtml)} style={secondaryBtnStyle}>
            {showHtml ? "Hide HTML" : "Show HTML"}
          </button>
        </div>
      )}

      {showHtml && data.html && (
        <pre style={{
          marginTop: 16,
          padding: 16,
          background: "rgba(0,0,0,0.3)",
          borderRadius: 12,
          fontSize: 11,
          color: COLORS.muted,
          overflow: "auto",
          maxHeight: 300,
        }}>
          {data.html}
        </pre>
      )}
    </div>
  );
}

function PitchDeckView({ data }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const slides = data.slides || [];
  const slide = slides[activeSlide];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 20, color: COLORS.text }}>{data.company_name}</h3>
        <p style={{ margin: "0 0 8px", fontSize: 14, color: COLORS.muted }}>{data.tagline}</p>
        {data.ask && <p style={{ margin: 0, fontSize: 13, color: COLORS.amber }}>Ask: {data.ask}</p>}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => setActiveSlide(i)}
            style={{
              background: i === activeSlide ? COLORS.indigo : COLORS.surface,
              border: `1px solid ${i === activeSlide ? COLORS.indigo : COLORS.border}`,
              borderRadius: 8,
              color: i === activeSlide ? "#fff" : COLORS.muted,
              fontSize: 12,
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            {s.number}
          </button>
        ))}
      </div>

      {slide && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 24 }}>
          <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 6 }}>Slide {slide.number}</div>
          <h4 style={{ margin: "0 0 16px", fontSize: 22, color: COLORS.text }}>{slide.title}</h4>
          <ul style={{ margin: "0 0 20px", paddingLeft: 20, fontSize: 15, color: COLORS.text, lineHeight: 1.8 }}>
            {slide.bullets?.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          {slide.speaker_notes && (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.amber, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                Speaker Notes
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.muted, margin: 0, fontStyle: "italic" }}>{slide.speaker_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const actionBtnStyle = {
  background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
  border: "none",
  borderRadius: 10,
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 18px",
  cursor: "pointer",
};

const secondaryBtnStyle = {
  background: "none",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  color: COLORS.muted,
  fontSize: 13,
  padding: "8px 18px",
  cursor: "pointer",
};

function ResultView({ toolId, data, onUsePivot }) {
  switch (toolId) {
    case "pivot": return <PivotView data={data} onUsePivot={onUsePivot} />;
    case "competitors": return <CompetitorsView data={data} />;
    case "icp": return <IcpView data={data} />;
    case "landing-page": return <LandingPageView data={data} />;
    case "pitch-deck": return <PitchDeckView data={data} />;
    default: return null;
  }
}

export default function IdeaDevelopmentPanel({
  validationId,
  idea,
  result,
  userPlan = "free",
  onUsePivot,
  onUpgrade,
}) {
  const [activeTool, setActiveTool] = useState("pivot");
  const [artifacts, setArtifacts] = useState({});
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");

  const loadCached = useCallback(async () => {
    if (!validationId || !/^\d+$/.test(String(validationId))) return;
    try {
      const data = await developmentApi.list(validationId);
      const map = {};
      for (const a of data.artifacts || []) {
        map[a.featureType] = a.result;
      }
      setArtifacts(map);
    } catch {
      /* ignore — may not have artifacts yet */
    }
  }, [validationId]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  const runTool = async (toolId, refresh = false) => {
    const tool = TOOLS.find((t) => t.id === toolId);
    if (!canAccess(userPlan, tool.minPlan)) {
      onUpgrade?.(tool.minPlan);
      return;
    }

    if (artifacts[toolId] && !refresh) {
      setActiveTool(toolId);
      return;
    }

    setActiveTool(toolId);
    setLoading(toolId);
    setError("");

    try {
      const payload = validationId && /^\d+$/.test(String(validationId))
        ? { validationId: Number(validationId), refresh }
        : { idea, result, refresh };

      const data = await developmentApi.generate(toolId, payload);
      setArtifacts((prev) => ({ ...prev, [toolId]: data.artifact.result }));
    } catch (err) {
      if (err.status === 402) {
        onUpgrade?.(err.data?.requiredPlan || "pro");
      } else {
        setError(err.message || "Failed to generate. Try again.");
      }
    } finally {
      setLoading(null);
    }
  };

  const activeData = artifacts[activeTool];

  return (
    <div
      style={{
        background: "rgba(245,158,11,0.04)",
        border: "1px solid rgba(245,158,11,0.2)",
        borderRadius: 20,
        padding: 28,
        marginBottom: 24,
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 24 }}>🔨</span>
          <h3 style={{ margin: 0, fontSize: 20, color: COLORS.text }}>Layer 2 — Idea Development</h3>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: COLORS.muted, lineHeight: 1.6 }}>
          Your idea needs work — let's make it stronger. Pick a tool below to pivot, research, and build.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 24 }}>
        {TOOLS.map((tool) => {
          const locked = !canAccess(userPlan, tool.minPlan);
          const hasResult = Boolean(artifacts[tool.id]);
          const isActive = activeTool === tool.id;
          const isLoading = loading === tool.id;

          return (
            <button
              key={tool.id}
              onClick={() => runTool(tool.id)}
              disabled={Boolean(loading)}
              style={{
                background: isActive ? "rgba(99,102,241,0.12)" : COLORS.surface,
                border: `1px solid ${isActive ? COLORS.indigo : COLORS.border}`,
                borderRadius: 14,
                padding: "14px 12px",
                cursor: loading ? "wait" : "pointer",
                textAlign: "left",
                opacity: locked ? 0.65 : 1,
                position: "relative",
              }}
            >
              {hasResult && !isLoading && (
                <span style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: COLORS.green,
                }} />
              )}
              <div style={{ fontSize: 22, marginBottom: 6 }}>{isLoading ? "⏳" : tool.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>{tool.label}</div>
              <div style={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.4 }}>{tool.desc}</div>
              {locked && (
                <div style={{ fontSize: 10, color: COLORS.amber, marginTop: 6, fontWeight: 600 }}>
                  {planLabel(tool.minPlan)}+
                </div>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 14, color: COLORS.red }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 32, marginBottom: 12, animation: "pulse 1.5s ease infinite" }}>🔨</div>
          <p style={{ fontSize: 14, color: COLORS.muted }}>{LOADING_LABELS[loading]}</p>
        </div>
      )}

      {!loading && activeData && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h4 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>
              {TOOLS.find((t) => t.id === activeTool)?.label}
            </h4>
            <button onClick={() => runTool(activeTool, true)} style={secondaryBtnStyle}>
              Regenerate ↻
            </button>
          </div>
          <ResultView toolId={activeTool} data={activeData} onUsePivot={onUsePivot} />
        </div>
      )}

      {!loading && !activeData && (
        <div style={{ textAlign: "center", padding: "20px", color: COLORS.muted, fontSize: 14 }}>
          Select a tool above to start developing your idea.
        </div>
      )}
    </div>
  );
}
